// The message and instructions the Studio composer sends with each agent step.
//
// Deliberately free of runtime imports — `PipelineStepConfig` is a type-only import, so
// nothing here pulls in RunView or the generated entities. That keeps the two decisions
// below testable in plain Jest, which is how the empty-message defect below was caught.
import type { PipelineStepConfig } from '../../types';

/**
 * Builds the instructions for one step: whatever the operator typed, plus context
 * contributed by steps that already ran. May legitimately be empty — a step with no
 * typed instructions and no upstream context has nothing to say.
 */
export function buildEnrichedInstructions(step: PipelineStepConfig, accPayload: Record<string, unknown>): string {
    const parts: string[] = [];

    if (step.instructions?.trim()) {
        parts.push(step.instructions.trim());
    }

    // Auto-enrich with context from previous steps
    const subject = accPayload['newsletterSubject'];
    if (subject) {
        parts.push(`A newsletter titled "${subject}" was just created.`);
    }
    const titles = accPayload['generatedBlogTitles'] as string[] | undefined;
    if (titles?.length) {
        parts.push(`Blog posts were generated: ${titles.join(', ')}.`);
    }
    const count = accPayload['generatedBlogCount'];
    if (count) {
        parts.push(`${count} blog post(s) are now published.`);
    }

    return parts.join(' ');
}

/**
 * The user message accompanying the RunAIAgent call. NEVER empty.
 *
 * MJ's `AgentRunner.RunAgentInConversation` rejects a falsy message outright —
 * "userMessage is required when conversationDetailId is not provided" — and it throws
 * before an AIAgentRun row exists. Because the composer fires the agent with
 * `fireAndForget` and then polls the database for that row, an empty message produces a
 * step stuck on "Running" until the poll times out, with the real error visible only in
 * the server log. Every step therefore falls back to naming its own agent.
 *
 * A non-empty message does not turn a Flow agent conversational: a Flow agent executes its
 * step graph regardless of the message text. Verified twice against the Content Pipeline —
 * on stage 2026-09-07 and in prod 2026-09-08 (agent run 4369EF13), both 9 of 9 steps.
 */
export function buildAgentUserMessage(step: PipelineStepConfig, instructions: string): string {
    const typed = instructions?.trim();
    if (typed) {
        return typed;
    }
    return `Run the ${step.agentName || 'selected'} agent.`;
}
